/**
 * FASE 5.8 — Anno dinamico "esercizio" per label wizard.
 *
 * Definizione (registrazione vocale utente):
 *   - "Esercizio in corso" = anno solare corrente
 *   - "Ultimo esercizio" = YEAR() - 1  (anno solare precedente)
 *   - "Penultimo esercizio" = YEAR() - 2
 *   - "Terzultimo esercizio" / "Terz'ultimo esercizio" = YEAR() - 3
 *
 * Usage:
 *   import { enrichLabelWithYear } from "@/lib/exerciseYear";
 *   <label>{enrichLabelWithYear(campo.label)}</label>
 */

/**
 * @param {number} offset  anni sottratti all'anno corrente (default 1 = "ultimo")
 * @returns {number}       anno solare
 */
export function exerciseYear(offset = 1) {
  return new Date().getFullYear() - offset;
}

/**
 * Compone "Base label (YYYY)".
 * @param {string} baseLabel
 * @param {number} offset
 * @returns {string}
 */
export function exerciseLabel(baseLabel, offset = 1) {
  return `${baseLabel} (${exerciseYear(offset)})`;
}

/**
 * Arricchisce automaticamente una label riconoscendo i pattern
 * "ultimo/penultimo/terzultimo esercizio" o "Esercizio 1/2/3"
 * e appendendo " (YYYY)" subito dopo il match.
 *
 * L'ordine dei pattern è deterministico:
 *   1. "Esercizio 3/2/1" (specifico) → offset 3/2/1
 *   2. "terz'ultimo esercizio" → offset 3
 *   3. "penultimo esercizio"   → offset 2
 *   4. "ultimo esercizio"      → offset 1
 *
 * Applica SOLO il primo pattern trovato (per evitare doppie iniezioni
 * in label composite tipo "Esercizio 1 (ultimo esercizio finanziario)").
 *
 * Se la label contiene già un anno a 4 cifre (`19xx`/`20xx`), la ritorna invariata.
 *
 * @param {string} label
 * @returns {string}
 */
export function enrichLabelWithYear(label) {
  if (!label || typeof label !== "string") return label;
  if (/\b(19|20)\d{2}\b/.test(label)) return label;

  const patterns = [
    { re: /(Esercizio 3)(?!\d)/, offset: 3 },
    { re: /(Esercizio 2)(?!\d)/, offset: 2 },
    { re: /(Esercizio 1)(?!\d)/, offset: 1 },
    { re: /(terz['\u2019]?ultimo esercizio|terzultimo esercizio)/i, offset: 3 },
    { re: /\b(penultimo esercizio)/i, offset: 2 },
    { re: /\b(ultimo esercizio)/i, offset: 1 },
  ];

  for (const { re, offset } of patterns) {
    if (re.test(label)) {
      return label.replace(re, `$1 (${exerciseYear(offset)})`);
    }
  }
  return label;
}
